import * as React from "react";
import { AnalyticsData } from "./Analytics";
// import { prettyLog } from "./helpers";

export interface AnalyticsProviderProps {
  division: "vol";
  country: "nz" | "au";
  site: "skvnz" | "skvau" | "nswgov";
  scriptSrc: string;
}

type AnalyticsLinkNameTypes =
  | "favourite-opportunity"
  | "favourite-organisation"
  | "remove-favourite-opportunity"
  | "remove-favourite-organisation"
  | "subscribe-to-new-opportunity-email"
  | "express-of-interest"
  | "survey-link-clicked";

// State context is immutable for now
type AnalyticsStateContext = AnalyticsProviderProps & {
  scriptLoaded: boolean;
};

interface AnalyticsMethods {
  trackPageLink: (link: AnalyticsLinkNameTypes) => void;
  trackPageView: (data: AnalyticsData) => void;
}

const AnalyticsContext = React.createContext<AnalyticsStateContext | undefined>(
  undefined
);

const AnalyticsMethods = React.createContext<AnalyticsMethods | undefined>(
  undefined
);

declare const global: {
  utag: any;
  analytics: any;
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  country,
  division,
  site,
  scriptSrc,
  children,
}) => {
  if (!country || !division || !site) {
    throw Error(
      "Analytics provider needs 'country', 'division', and 'site' props"
    );
  }

  const queue = React.useRef<AnalyticsData[]>([]);
  const [scriptLoaded, setScriptLoaded] = React.useState(false);

  /* methods */

  const trackPageLink = React.useCallback(
    (link: AnalyticsLinkNameTypes) => {
      if (global.utag && scriptLoaded) {
        global.analytics.linkName = link;
        global.utag.link(global.analytics);

        // if (process.env.NODE_ENV !== "production") {
        //   prettyLog("link", global.analytics);
        // }
      }
    },
    [scriptLoaded]
  );

  const trackPageView = React.useCallback(
    (data?: AnalyticsData): void => {
      if (global.utag && scriptLoaded) {
        global.analytics = Object.assign({}, { country, division, site }, data);
        global.utag.view(global.analytics);

        // if (process.env.NODE_ENV !== "production") {
        //   prettyLog("page-view", global.analytics);
        // }
      } else {
        //prettyLog("queuing", data);
        queue.current = [...queue.current, data];
      }
    },
    [country, division, site, scriptLoaded]
  );

  React.useEffect(() => {
    // initialise default global.analytics object
    global.analytics = {
      country,
      division,
      site,
    };
  }, [country, division, site]);

  React.useEffect(() => {
    // load tracking script

    if (scriptSrc && typeof scriptSrc === "string") {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.type = "text/javascript";
      script.async = true;
      script.onload = (): void => {
        setScriptLoaded(true);
      };

      const topScript = document.getElementsByTagName("script")[0];
      topScript.parentNode.insertBefore(script, topScript);
    }
  }, [scriptSrc]);

  React.useEffect(() => {
    // process queue
    if (scriptLoaded) {
      queue.current.forEach((data) => trackPageView(data));
      queue.current = [];
    }
  }, [scriptLoaded, trackPageView]);

  return (
    <AnalyticsContext.Provider
      value={{ country, division, site, scriptSrc, scriptLoaded }}
    >
      <AnalyticsMethods.Provider value={{ trackPageLink, trackPageView }}>
        {children}
      </AnalyticsMethods.Provider>
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsUtil = () => {
  const analyticsUtils = React.useContext(AnalyticsMethods);

  if (analyticsUtils === undefined) {
    throw Error("useAnalyticsUtil used outside an <AnalyticsProvider />");
  }

  return analyticsUtils;
};
